// Typed wrapper for GET /v1/merchants/:id/paysmart-balance.
//
// Live call to the bank rail's GetMerchantBalance via the merchant's active
// IDFC rail. Shape mirrors the Buckzy balance endpoint so the dashboard can
// render both surfaces with the same primitive — amount_minor is a JSON
// STRING on the wire (bigint-safe; CLAUDE.md rule 6) and we promote to
// bigint at the deserialization boundary.

import { apiClient } from "./client";

/**
 * BalanceSource — every value the backend's paysmart-balance handler can
 * return on the wire (paysmart_balance.go). The same TS literal type is
 * reused by RawPaysmartBalance and PaysmartBalance so a new source value
 * only has to be added once.
 *
 * - live           — live upstream rail GetMerchantBalance result.
 * - not_connected  — no active IDFC bank rail for this merchant.
 * - hidden         — superadmin set merchants.balance_display='hidden';
 *                    no upstream call, no number to display.
 * - ledger         — operator-managed balance source. amount_minor is
 *                    SUM(merchant_ledger_entries) - SUM(successful payouts).
 *                    When no ledger rows exist yet, amount_minor="0" and
 *                    error="no_ledger_entries" so the panel can render
 *                    "not initialized" copy instead of a misleading ₹0.
 * - error          — upstream or local error; error field carries detail.
 */
export type BalanceSource =
  | "live"
  | "not_connected"
  | "hidden"
  | "ledger"
  | "error";

/**
 * Wire shape — backend serialises amount_minor via strconv.FormatInt so
 * the value is always a string when present. source classifies the
 * outcome, error carries an opaque diagnostic when the call failed.
 */
interface RawPaysmartBalance {
  amount_minor?: string;
  currency?: string;
  as_of?: string;
  source: BalanceSource;
  error?: string;
  rail_label?: string;
}

/**
 * PaysmartBalance — typed balance + provenance.
 *
 * Variants by source:
 *
 * - live           → amount_minor is non-null bigint, as_of set.
 * - not_connected  → no active IDFC bank rail; amount_minor null.
 * - hidden         → operator opted the merchant out; amount_minor null.
 * - ledger         → operator-managed. amount_minor is the computed bigint.
 *                    error="no_ledger_entries" signals the ledger has no
 *                    rows yet — the panel should render "not initialized"
 *                    rather than ₹0 even though amount_minor is 0n.
 * - error          → live call failed; error carries the diagnostic
 *                    (upstream rail code+message or credential decryption
 *                    failure). amount_minor null.
 *
 * Render via fmtCcyMinor when amount_minor is non-null. Never float-coerce.
 */
export interface PaysmartBalance {
  amount_minor: bigint | null;
  currency: string | null;
  as_of: string | null;
  source: BalanceSource;
  error: string | null;
  rail_label: string | null;
}

/**
 * getPaysmartBalance — GET /v1/merchants/:merchantId/paysmart-balance
 *
 * Viewer+ membership required. Admin short-circuits. Promotes the wire
 * amount_minor from string to bigint; null when source !== "live".
 */
export async function getPaysmartBalance(
  merchantId: number,
): Promise<PaysmartBalance> {
  const resp = await apiClient.get<RawPaysmartBalance>(
    `/v1/merchants/${merchantId}/paysmart-balance`,
  );
  const raw = resp.data;
  return {
    amount_minor: raw.amount_minor ? BigInt(raw.amount_minor) : null,
    currency: raw.currency ?? null,
    as_of: raw.as_of ?? null,
    source: raw.source,
    error: raw.error ?? null,
    rail_label: raw.rail_label ?? null,
  };
}
